import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import {
  api,
  ApiError,
  getActiveCompany,
  setActiveCompany,
  type SessionUser,
  type CompanySummary,
} from './api'

export interface ActiveCompany {
  id: string
  name: string
}

interface AuthState {
  user: SessionUser | null
  company: CompanySummary | null
  loading: boolean
  // Company a super_admin is currently viewing (via the switcher). null for
  // company users, whose scope is implicitly their own company.
  activeCompany: ActiveCompany | null
  enterCompany: (company: ActiveCompany) => void
  exitCompany: () => void
  login: (email: string, password: string) => Promise<SessionUser>
  register: (payload: {
    companyName: string
    name: string
    email: string
    password: string
  }) => Promise<SessionUser>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null)
  const [company, setCompany] = useState<CompanySummary | null>(null)
  const [loading, setLoading] = useState(true)
  // Restored synchronously so route guards see it on first render.
  const [activeCompany, setActive] = useState<ActiveCompany | null>(() => getActiveCompany())

  function enterCompany(c: ActiveCompany) {
    setActiveCompany(c)
    setActive(c)
    // Refresh scope so `company` (form link, etc.) reflects the viewed company.
    api.me().then((me) => setCompany(me.company)).catch(() => {})
  }
  function exitCompany() {
    setActiveCompany(null)
    setActive(null)
    setCompany(null)
  }

  useEffect(() => {
    let active = true
    api
      .me()
      .then((res) => {
        if (!active) return
        setUser(res.user)
        setCompany(res.company)
      })
      .catch((err) => {
        // 401 just means not logged in; anything else we also treat as logged-out.
        if (!(err instanceof ApiError)) console.error(err)
      })
      .finally(() => active && setLoading(false))
    return () => {
      active = false
    }
  }, [])

  async function login(email: string, password: string) {
    const res = await api.login(email, password)
    setUser(res.user)
    // Refresh company context for company users.
    const me = await api.me().catch(() => null)
    setCompany(me?.company ?? null)
    return res.user
  }

  // Registration signs the new admin in, so hydrate the same state as login.
  async function register(payload: {
    companyName: string
    name: string
    email: string
    password: string
  }) {
    const res = await api.register(payload)
    setUser(res.user)
    const me = await api.me().catch(() => null)
    setCompany(me?.company ?? null)
    return res.user
  }

  async function logout() {
    await api.logout().catch(() => {})
    setUser(null)
    setCompany(null)
    exitCompany()
  }

  return (
    <AuthContext.Provider
      value={{ user, company, loading, activeCompany, enterCompany, exitCompany, login, register, logout }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
